import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  deleteAlarmInfo,
  GetAlarmResponse,
  AlarmRequest,
  createAlarm,
  AlarmTableItem,
  getAllAlarmsTable,
  updateAlarm,
} from '@app/api/alarm.api'

interface AlarmInfoState {
  data: GetAlarmResponse[]
  isLoading: boolean
  error: string | undefined
  isLoadingAlarms: boolean
  errorAlarms: string | undefined
  alarmsTable: AlarmTableItem[]
}

const initialState: AlarmInfoState = {
  isLoading: false,
  error: undefined,
  data: [],
  isLoadingAlarms: false,
  errorAlarms: undefined,
  alarmsTable: [],
}

export interface AlarmInfoUpdateRequest {
  id: string
  data: AlarmRequest
}

export const getAllAlarmsTableData = createAsyncThunk<AlarmTableItem[]>(
  'alarmInfo/getAllAlarmsTableData',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllAlarmsTable()
    } catch (err: any) {
      return rejectWithValue(err.message)
    }
  },
)

export const createAlarmData = createAsyncThunk<GetAlarmResponse, AlarmRequest>(
  'alarm/createAlarm',
  (createAlarmInfoPayload, { rejectWithValue }) =>
    createAlarm(createAlarmInfoPayload)
      .then((res) => {
        return res
      })
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const updateAlarmData = createAsyncThunk<GetAlarmResponse, AlarmInfoUpdateRequest>(
  'alarm/updateAlarm',
  (updateAlarmPayload, { rejectWithValue }) =>
    updateAlarm(updateAlarmPayload.id, updateAlarmPayload.data)
      .then((res) => {
        return res
      })
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const deleteAlarmInfoData = createAsyncThunk<GetAlarmResponse, string>(
  'alarmInfo/deleteAlarmInfo',
  (id, { rejectWithValue }) =>
    deleteAlarmInfo(id)
      .then((res) => res)
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const alarmSlice = createSlice({
  name: 'alarmInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(createAlarmData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(createAlarmData.fulfilled, (state, action: PayloadAction<GetAlarmResponse>) => {
        state.isLoading = false
        state.data.push(action.payload)

        if (state.alarmsTable) {
          state.alarmsTable = [
            ...state.alarmsTable,
            {
              id: action.payload.id,
              name: action.payload.name,
              type: action.payload.type,
            },
          ]
        }
      })
      .addCase(createAlarmData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(updateAlarmData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(updateAlarmData.fulfilled, (state, action: PayloadAction<GetAlarmResponse>) => {
        state.isLoading = false
        const index = state.data.findIndex((item) => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
        if (state.alarmsTable) {
          state.alarmsTable = state.alarmsTable.map((alarm) =>
            alarm.id === action.payload.id
              ? {
                  id: action.payload.id,
                  name: action.payload.name,
                  type: action.payload.type,
                }
              : alarm,
          )
        }
      })
      .addCase(updateAlarmData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(deleteAlarmInfoData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(deleteAlarmInfoData.fulfilled, (state, action: PayloadAction<GetAlarmResponse>) => {
        state.isLoading = false
        state.data = state.data.filter((inverter) => inverter.id !== action.payload.id)

        if (state.alarmsTable) {
          state.alarmsTable = state.alarmsTable.filter((alarm) => alarm.id !== action.payload.id)
        }
      })
      .addCase(deleteAlarmInfoData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(getAllAlarmsTableData.pending, (state) => {
        state.isLoadingAlarms = true
        state.errorAlarms = undefined
      })
      .addCase(getAllAlarmsTableData.fulfilled, (state, action: PayloadAction<AlarmTableItem[]>) => {
        state.isLoadingAlarms = false
        state.alarmsTable = action.payload
      })
      .addCase(getAllAlarmsTableData.rejected, (state, action) => {
        state.isLoadingAlarms = false
        state.errorAlarms = action.payload as string
      })
  },
})

export default alarmSlice.reducer
